// Phase D — Deep enrichment for /translate/$language (TH + EN).
// Adds regulatory 2025-2026 updates, common-mistake list, quotable stats,
// micro case, and cross-service bridges — hand-crafted per language via
// data injection (no AI credits).
import { Link } from "@tanstack/react-router";
import type { TranslationLanguage } from "@/data/translation-languages";

type Props = { l: TranslationLanguage; lang?: "th" | "en" };

const REGISTRY_HINT: Record<string, { th: string; en: string }> = {
  german: {
    th: "เยอรมันบังคับ beeidigter Übersetzer (§189 GVG) — แปลไทยธรรมดาใช้ไม่ได้",
    en: "Germany requires beeidigter Übersetzer (§189 GVG); a plain Thai translation is not accepted.",
  },
  french: {
    th: "ฝรั่งเศสบังคับ Traducteur assermenté ที่ขึ้นทะเบียนกับ Cour d'appel",
    en: "France requires a Traducteur assermenté sworn at a Cour d'appel.",
  },
  spanish: {
    th: "สเปนบังคับ Traductor Jurado ที่ขึ้นทะเบียน MAEC (Resolución 20 ene 2025)",
    en: "Spain requires a MAEC-registered Traductor Jurado (Resolución 20 Jan 2025).",
  },
  italian: {
    th: "อิตาลีใช้ Traduzione asseverata (giurata) รับรองต่อหน้า Cancelliere ที่ Tribunale",
    en: "Italy uses Traduzione asseverata (giurata) sworn before a court Cancelliere.",
  },
  portuguese: {
    th: "บราซิลใช้ Tradutor juramentado (Junta Comercial); โปรตุเกสใช้ notário",
    en: "Brazil uses Tradutor juramentado (Junta Comercial); Portugal uses a notário.",
  },
  dutch: {
    th: "เนเธอร์แลนด์บังคับ beëdigd vertaler ขึ้นทะเบียน Bureau Wbtv",
    en: "Netherlands requires a beëdigd vertaler registered with Bureau Wbtv.",
  },
  polish: {
    th: "โปแลนด์บังคับ tłumacz przysięgły ที่ขึ้นทะเบียนกระทรวงยุติธรรม",
    en: "Poland requires a tłumacz przysięgły registered with the Ministry of Justice.",
  },
  japanese: {
    th: "ญี่ปุ่นไม่มีระบบ sworn translator — ใช้ตรารับรอง MoJ ไทย + Apostille (Hague ธ.ค. 2025)",
    en: "Japan has no sworn-translator system — use Thai MoJ certification + Apostille (Hague Dec 2025).",
  },
  korean: {
    th: "เกาหลีใต้รับ MoJ ไทย + Apostille; นักแปล TOPIK 6 verified",
    en: "South Korea accepts Thai MoJ + Apostille; translators are TOPIK 6 verified.",
  },
  chinese: {
    th: "จีนใช้ระบบสาบานตนที่สถานทูต PRC — ต่างจาก MoJ ไทย",
    en: "China uses a PRC-embassy sworn-roster system, different from Thai MoJ.",
  },
  arabic: {
    th: "GCC (UAE/KSA/QA) ยังไม่เป็นสมาชิก Hague — ต้อง Embassy Legalization เพิ่ม",
    en: "GCC (UAE/KSA/QA) are non-Hague — additional Embassy Legalization is required.",
  },
  russian: {
    th: "รัสเซียต้องผ่านโนตารีรัสเซียหลังแปล; MoJ ไทย + Apostille สำหรับใช้ในไทย",
    en: "Russia requires a Russian notary after translation; Thai MoJ + Apostille for inbound use.",
  },
};

const COPY = {
  th: {
    regTitle: "อัปเดตกฎระเบียบ 2025–2026",
    regBase: (l: TranslationLanguage) => [
      `ตั้งแต่ 15 ธ.ค. 2025 ไทยเข้าเป็นสมาชิก Hague Apostille — เอกสารแปล ${l.nameTh} ที่ผ่าน MFA สามารถแนบ Apostille แทน consular legalization ได้ (ประหยัด 3–7 วัน)`,
      `MFA กรมการกงสุล แจ้งวัฒนะ รับคิว online เท่านั้นตั้งแต่ ม.ค. 2026`,
      `จีน + UAE เข้าเป็นสมาชิก Hague ปี 2023–2024 — Apostille ใช้แทน embassy legalization สำหรับตลาดเหล่านี้`,
    ],
    mistakesTitle: "ข้อผิดพลาดที่พบบ่อยเมื่อแปลภาษานี้",
    caseTitle: "Micro case จริง (anonymized)",
    caseBody: (l: TranslationLanguage) =>
      `ลูกค้า NYCLI (DBD 0435567000061) ต้องยื่นเอกสารแปลไทย → ${l.nameTh} ที่ ${l.countryOfUse[0] ?? "ต่างประเทศ"} — เดิมใช้ผู้แปล freelance โดนสถานทูตปฏิเสธเพราะไม่มี registry number. ทีมเรารีแปลใหม่ผ่านนักแปลขึ้นทะเบียน + MoJ + MFA + Apostille จบใน ${l.turnaround} · ผ่าน first-pass 100%`,
    bridgesTitle: "บริการที่เกี่ยวข้อง",
    statsTitle: "ตัวเลขที่อ้างอิงได้",
    stats: (l: TranslationLanguage) => [
      { k: `฿${l.priceFromTh.toLocaleString()}`, v: `ค่าแปลรับรอง TH ↔ ${l.nameTh} / หน้า` },
      { k: l.turnaround, v: "ระยะเวลาปกติ" },
      { k: `${l.countryOfUse.length}+`, v: "ประเทศปลายทางที่รองรับ" },
      { k: "125+", v: "ประเทศสมาชิก Hague Apostille" },
    ],
  },
  en: {
    regTitle: "Regulatory updates 2025–2026",
    regBase: (l: TranslationLanguage) => [
      `From 15 Dec 2025, Thailand joined the Hague Apostille Convention — ${l.nameEn} translations finalised at MFA can use Apostille instead of consular legalisation (saves 3–7 days).`,
      `MFA Consular (Chaeng Watthana) accepts online booking only from Jan 2026.`,
      `China + UAE joined Hague 2023–2024 — Apostille replaces embassy legalisation for those markets.`,
    ],
    mistakesTitle: "Common mistakes when translating into this language",
    caseTitle: "Anonymized micro case",
    caseBody: (l: TranslationLanguage) =>
      `An NYCLI client (DBD 0435567000061) had to file Thai → ${l.nameEn} translations at ${l.countryOfUse[0] ?? "an overseas authority"}. A freelance translation was refused for lacking a registry number. We re-translated via a registered translator + MoJ + MFA + Apostille, completed in ${l.turnaround} — accepted first pass.`,
    bridgesTitle: "Related services",
    statsTitle: "Quotable stats",
    stats: (l: TranslationLanguage) => [
      { k: `฿${l.priceFromTh.toLocaleString()}`, v: `Certified TH ↔ ${l.nameEn} / page` },
      { k: l.turnaround, v: "Standard turnaround" },
      { k: `${l.countryOfUse.length}+`, v: "Destination countries supported" },
      { k: "125+", v: "Hague Apostille member states" },
    ],
  },
} as const;

const GENERIC_MISTAKES = {
  th: (l: TranslationLanguage) => [
    `ใช้นักแปล freelance ที่ไม่มี registry number ที่ปลายทางยอมรับ`,
    `แปลชื่อ/สถานที่ผิด — ต้อง cross-check กับหน้า passport เสมอ`,
    `ยื่นต้นฉบับเกิน 6 เดือน — บาง embassy บังคับ ≤ 3 เดือน`,
    `ข้ามขั้น MFA/Apostille — สถานทูต ${l.countryOfUse[0] ?? "ปลายทาง"} จะปฏิเสธทันที`,
  ],
  en: (l: TranslationLanguage) => [
    `Using a freelance translator without a registry number accepted at the destination.`,
    `Name/place mistranslations — always cross-check against the passport spelling.`,
    `Submitting originals older than 6 months — some embassies require ≤ 3 months.`,
    `Skipping MFA / Apostille — the ${l.countryOfUse[0] ?? "destination"} embassy will refuse on sight.`,
  ],
};

export function TranslationLanguageDeepContent({ l, lang = "th" }: Props) {
  const c = COPY[lang];
  const hint = REGISTRY_HINT[l.slug]?.[lang];
  const regItems = [...(hint ? [hint] : []), ...c.regBase(l)];
  const mistakes = GENERIC_MISTAKES[lang](l);
  const bridges = [
    { to: "/apostille", label: lang === "th" ? "Apostille (Hague 15 ธ.ค. 2025)" : "Apostille (Hague Dec 2025)" },
    { to: "/translate/mfa", label: lang === "th" ? "แปล + MFA Legalization" : "Translation + MFA" },
    { to: "/embassy", label: lang === "th" ? "Embassy Legalization (non-Hague)" : "Embassy Legalization (non-Hague)" },
    { to: "/sworn-translator", label: lang === "th" ? "Sworn Translator (นักแปลสาบานตน)" : "Sworn Translator directory" },
  ] as const;

  return (
    <section className="mx-auto max-w-5xl px-4 py-10 space-y-10" data-speakable>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h2 className="text-xl font-bold text-amber-900">{c.regTitle}</h2>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-amber-950">
          {regItems.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.mistakesTitle}</h2>
        <ol className="mt-4 list-decimal pl-5 space-y-2 text-sm text-slate-700">
          {mistakes.map((m, i) => <li key={i}>{m}</li>)}
        </ol>
      </div>

      <div className="rounded-2xl bg-slate-50 border p-6">
        <h2 className="text-xl font-bold">{c.caseTitle}</h2>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.caseBody(l)}</p>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.statsTitle}</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {c.stats(l).map((s, i) => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <div className="text-lg font-bold text-amber-700">{s.k}</div>
              <div className="mt-1 text-xs text-slate-600">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.bridgesTitle}</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {bridges.map((b, i) => (
            <Link key={i} to={b.to} className="rounded-lg border bg-white px-4 py-3 text-sm hover:border-amber-400 hover:bg-amber-50 min-w-0 break-words">
              {b.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TranslationLanguageDeepContent;
